import { WebAudioSynth } from './audio.js';
import { WorldManager } from './world.js';
import { multiplayer } from './multiplayer/MultiplayerManager.js';

// Register PWA Service Worker for true offline installability
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => console.log('PWA Service Worker registered successfully:', reg.scope))
      .catch((err) => console.warn('PWA Service Worker registration failed:', err));
  });
}

// Track beforeinstallprompt events to trigger PWA installation
let deferredPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent Chrome 67 and earlier from automatically showing the prompt
  e.preventDefault();
  // Stash the event so it can be triggered later.
  deferredPrompt = e;
  // Update UI to show the install section in the Settings panel
  const pwaSection = document.getElementById('pwa-install-section');
  if (pwaSection) {
    pwaSection.style.display = 'block';
  }
});

window.addEventListener('appinstalled', (evt) => {
  console.log('FRONTROOMS was installed successfully.');
  const pwaSection = document.getElementById('pwa-install-section');
  if (pwaSection) {
    pwaSection.style.display = 'none';
  }
  deferredPrompt = null;
});

// Translation Dictionary
const TRANSLATIONS = {
  en: {
    "menu-subtitle": "Liminal Space Experience",
    "loader-status": "INITIALIZING SYSTEMS...",
    "btn-enter": "Enter Frontrooms",
    "btn-settings": "SETTINGS",
    "btn-exit": "EXIT",
    
    "settings-title": "SETTINGS",
    "settings-subtitle": "Signal Calibration",
    "settings-lang-title": "Language / Idioma",
    "settings-display-title": "Display Mode",
    "settings-fullscreen": "Enable Fullscreen Play Mode (PWA Recommended)",
    "settings-calibration-title": "Experience Calibration",
    "setting-spawn": "Spawn Sector / Room",
    "setting-hum": "HVAC Vent / Fluorescent Hum",
    "setting-footsteps": "Footstep Dampener",
    "setting-music": "Ambient Theme Music",
    "setting-fov": "Field of View (FOV)",
    "setting-bobbing": "Head Bobbing Intensity",
    "setting-flicker": "Enable Bulb Degradation & Flickers (Atmospheric Events)",
    "guide-title": "Navigation Protocols",
    "guide-walk": "Walk [WASD]",
    "guide-look": "Look [MOUSE]",
    "guide-interact": "Interact [E]",
    "guide-pause": "Pause [ESC]",
    "btn-back": "Back to Menu",

    "opt-zone0": "INFINITE LOBBY (ZONE 0)",
    "opt-zone1": "SILENT MALL (ZONE 1)",
    "opt-zone2": "IMPOSSIBLE HOTEL (ZONE 2)",
    "opt-zone3": "SLEEPY AIRPORT (ZONE 3)",
    "opt-zone4": "CORPORATE BLOCK (ZONE 4)",
    "opt-zone5": "ENDLESS LIBRARY (ZONE 5)",
    "opt-zone6": "DATA CENTER (ZONE 6)",
    "opt-zone7": "SUBTERRANEAN POOL (ZONE 7)",
    "opt-zone8": "EMPTY GREENHOUSE (ZONE 8)",
    "opt-zone9": "ART GALLERY (ZONE 9)",

    "pause-title": "SYSTEMS PAUSED",
    "pause-subtitle": "Signal Interrupted",
    "btn-resume": "Resume Exploration",
    "btn-exit-pause": "EXIT TO MENU",
    "pause-settings-title": "Adjust Settings",
    "setting-hum-short": "Ambient Hum Volume",
    "setting-footsteps-short": "Footstep Volume",
    "setting-music-short": "Ambient Music Volume",
    "setting-fov-short": "Field of View",
    "pause-disclaimer": "Press ESC or click Resume to restore pointer lock and resume exploration.",
    
    "rotate-title": "ROTATION REQUIRED",
    "rotate-subtitle": "PLEASE ROTATE YOUR DEVICE HORIZONTALLY TO ACCESS THE EXPERIENCE",
    "interact-prompt": "PRESS [E] TO INTERACT",
    "hud-rec": "REC",
    "hud-watermark": "FRONTROOMS ARCHIVE",

    // In-game zones
    "LOBBY INFINITO": "INFINITE LOBBY",
    "SHOPPING SILENCIOSO": "SILENT MALL",
    "HOTEL IMPOSIBLE": "IMPOSSIBLE HOTEL",
    "AEROPUERTO DORMIDO": "SLEEPY AIRPORT",
    "CENTRO CORPORATIVO": "CORPORATE BLOCK",
    "BIBLIOTECA INTERMINABLE": "ENDLESS LIBRARY",
    "CENTRO DE DATOS": "DATA CENTER",
    "PISCINA SUBTERRÁNEA": "SUBTERRANEAN POOL",
    "INVERNADERO VACÍO": "EMPTY GREENHOUSE",
    "GALERÍA DE ARTE": "ART GALLERY",

    // Signboard terms
    "EXIT": "EXIT",
    "LIFT": "LIFT",
    "EMPTY CABIN INBOUND": "EMPTY CABIN INBOUND",
    "CONFERENCE HALLS": "CONFERENCE HALLS",
    "RESTROOMS": "RESTROOMS",
    "TICKETS": "TICKETS",
    "AIRPORT TERMINAL": "AIRPORT TERMINAL",
    "BOARDING GATES": "BOARDING GATES",
    "TICKET SHOP": "TICKET SHOP",
    "settings-install-title": "Application Installation",
    "settings-install-desc": "Install FRONTROOMS directly on your device to enjoy offline procedural play, standalone viewport boundaries, and performance optimization.",
    "btn-pwa-install": "Install WebApp",
    "guide-modal-title": "NATIVE INSTALLATION",
    "guide-modal-subtitle": "Download Liminal Signal",
    "guide-android-title": "ANDROID / CHROME",
    "guide-android-desc": "Tap the browser menu icon (three dots) in the top-right corner, then select <strong>\"Install app\"</strong> or <strong>\"Add to Home screen\"</strong>.",
    "guide-ios-title": "IPHONE / IPAD (SAFARI)",
    "guide-ios-desc": "Tap the <strong>Share</strong> button (square with an up arrow) at the bottom toolbar, scroll down, and select <strong>\"Add to Home Screen\"</strong> (Agregar a pantalla de inicio).",
    "guide-desktop-title": "DESKTOP (CHROME / EDGE)",
    "guide-desktop-desc": "Click the <strong>Install Icon</strong> (monitor with down arrow) inside the browser address bar at the top, or select browser menu -> <strong>\"Install FRONTROOMS\"</strong>.",
    "btn-close": "CLOSE",
    "banner-title": "FRONTROOMS INSTALLATION",
    "banner-desc": "Add to Home Screen for native fullscreen play & offline liminal exploration.",
    "btn-banner-install": "INSTALL",
    "settings-multiplayer-title": "Experimental Multiplayer Protocol",
    "setting-multiplayer-enable": "Enable Multiplayer Connection Layer",
    "setting-nickname": "Player Nickname",
    "setting-multiplayer-voice": "Enable Spatial Voice Chat (Mic Required)",
    "setting-tag-distance": "Name Tag Range",
    "setting-signaling-url": "Signaling Server URL"
  },
  es: {
    "menu-subtitle": "Experiencia de Espacio Liminal",
    "loader-status": "INICIALIZANDO SISTEMAS...",
    "btn-enter": "Entrar a Frontrooms",
    "btn-settings": "AJUSTES",
    "btn-exit": "SALIR",
    
    "settings-title": "AJUSTES",
    "settings-subtitle": "Calibración de Señal",
    "settings-lang-title": "Language / Idioma",
    "settings-display-title": "Modo de Pantalla",
    "settings-fullscreen": "Activar Pantalla Completa (PWA Recomendado)",
    "settings-calibration-title": "Calibración de Experiencia",
    "setting-spawn": "Sector de Aparición / Habitación",
    "setting-hum": "Zumbido de Fluorescentes / HVAC",
    "setting-footsteps": "Amortiguador de Pisadas",
    "setting-music": "Música de Ambiente",
    "setting-fov": "Campo de Visión (FOV)",
    "setting-bobbing": "Intensidad de Cabeceo",
    "setting-flicker": "Activar Degradación y Parpadeo de Luces (Eventos Atmosféricos)",
    "guide-title": "Protocolos de Navegación",
    "guide-walk": "Caminar [WASD]",
    "guide-look": "Mirar [RATÓN]",
    "guide-interact": "Interactuar [E]",
    "guide-pause": "Pausa [ESC]",
    "btn-back": "Volver al Menú",

    "opt-zone0": "LOBBY INFINITO (ZONE 0)",
    "opt-zone1": "SHOPPING SILENCIOSO (ZONE 1)",
    "opt-zone2": "HOTEL IMPOSIBLE (ZONE 2)",
    "opt-zone3": "AEROPUERTO DORMIDO (ZONE 3)",
    "opt-zone4": "CENTRO CORPORATIVO (ZONE 4)",
    "opt-zone5": "BIBLIOTECA INTERMINABLE (ZONE 5)",
    "opt-zone6": "CENTRO DE DATOS (ZONE 6)",
    "opt-zone7": "PISCINA SUBTERRÁNEA (ZONE 7)",
    "opt-zone8": "INVERNADERO VACÍO (ZONE 8)",
    "opt-zone9": "GALERÍA DE ARTE (ZONE 9)",

    "pause-title": "SISTEMAS EN PAUSA",
    "pause-subtitle": "Señal Interrumpida",
    "btn-resume": "Reanudar Exploración",
    "btn-exit-pause": "SALIR AL MENÚ",
    "pause-settings-title": "Ajustar Configuración",
    "setting-hum-short": "Zumbido de Ambiente",
    "setting-footsteps-short": "Volumen de Pisadas",
    "setting-music-short": "Música de Ambiente",
    "setting-fov-short": "Campo de Visión",
    "pause-disclaimer": "Presiona ESC o clic en Reanudar para capturar el cursor y continuar.",
    
    "rotate-title": "ROTACIÓN REQUERIDA",
    "rotate-subtitle": "POR FAVOR GIRA TU DISPOSITIVO HORIZONTALMENTE PARA ACCEDER A LA EXPERIENCIA",
    "interact-prompt": "PRESIONA [E] PARA INTERACTUAR",
    "hud-rec": "GRA",
    "hud-watermark": "ARCHIVO DE FRONTROOMS",

    // In-game zones
    "LOBBY INFINITO": "LOBBY INFINITO",
    "SHOPPING SILENCIOSO": "SHOPPING SILENCIOSO",
    "HOTEL IMPOSIBLE": "HOTEL IMPOSIBLE",
    "AEROPUERTO DORMIDO": "AEROPUERTO DORMIDO",
    "CENTRO CORPORATIVO": "CENTRO CORPORATIVO",
    "BIBLIOTECA INTERMINABLE": "BIBLIOTECA INTERMINABLE",
    "CENTRO DE DATOS": "CENTRO DE DATOS",
    "PISCINA SUBTERRÁNEA": "PISCINA SUBTERRÁNEA",
    "INVERNADERO VACÍO": "INVERNADERO VACÍO",
    "GALERÍA DE ARTE": "GALERÍA DE ARTE",

    // Signboard terms
    "EXIT": "SALIDA",
    "LIFT": "ASCENSOR",
    "EMPTY CABIN INBOUND": "CABINA VACÍA EN CAMINO",
    "CONFERENCE HALLS": "SALAS DE CONFERENCIA",
    "RESTROOMS": "SERVICIOS",
    "TICKETS": "BOLETOS",
    "AIRPORT TERMINAL": "TERMINAL DE AEROPUERTO",
    "BOARDING GATES": "PUERTAS DE EMBARQUE",
    "TICKET SHOP": "BOLETERÍA",
    "settings-install-title": "Instalación de Aplicación",
    "settings-install-desc": "Instala FRONTROOMS directamente en tu dispositivo para disfrutar de juego procedural sin conexión, límites de pantalla independientes y optimización de rendimiento.",
    "btn-pwa-install": "Instalar WebApp",
    "guide-modal-title": "INSTALACIÓN NATIVA",
    "guide-modal-subtitle": "Descargar Señal Liminal",
    "guide-android-title": "ANDROID / CHROME",
    "guide-android-desc": "Toca el ícono de menú del navegador (tres puntos) en la esquina superior derecha, luego selecciona <strong>\"Instalar aplicación\"</strong> o <strong>\"Agregar a pantalla de inicio\"</strong>.",
    "guide-ios-title": "IPHONE / IPAD (SAFARI)",
    "guide-ios-desc": "Toca el botón <strong>Compartir</strong> (cuadrado con flecha hacia arriba) en la barra de herramientas inferior, desplázate hacia abajo y selecciona <strong>\"Agregar a pantalla de inicio\"</strong>.",
    "guide-desktop-title": "ESCRITORIO (CHROME / EDGE)",
    "guide-desktop-desc": "Haz clic en el <strong>Ícono de Instalación</strong> (monitor con flecha hacia abajo) dentro de la barra de direcciones del navegador en la parte superior, o selecciona el menú del navegador -> <strong>\"Instalar FRONTROOMS\"</strong>.",
    "btn-close": "CERRAR",
    "banner-title": "INSTALACIÓN DE FRONTROOMS",
    "banner-desc": "Añade a la pantalla de inicio para jugar a pantalla completa y sin conexión.",
    "btn-banner-install": "INSTALAR",
    "settings-multiplayer-title": "Protocolo Multijugador Experimental",
    "setting-multiplayer-enable": "Activar Capa de Conexión Multijugador",
    "setting-nickname": "Nombre del Jugador",
    "setting-multiplayer-voice": "Activar Chat de Voz Espacial (Requiere Micrófono)",
    "setting-tag-distance": "Rango de Etiquetas de Nombre",
    "setting-signaling-url": "Servidor de Señalización (URL)"
  }
};

// Global translation helpers
window.currentLanguage = 'en';
window.translate = function(key) {
  const lang = window.currentLanguage || 'en';
  return (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) ? TRANSLATIONS[lang][key] : key;
};

window.switchLanguage = function(lang) {
  window.currentLanguage = lang;

  // Sync checkboxes and labels in settings UI
  const toggle = document.getElementById('language-toggle');
  if (toggle) {
    toggle.checked = (lang === 'es');
  }

  const langLabels = document.querySelectorAll('.lang-label');
  langLabels.forEach(lbl => {
    if (lbl.textContent.includes('ENGLISH')) {
      if (lang === 'en') lbl.classList.add('active');
      else lbl.classList.remove('active');
    } else if (lbl.textContent.includes('ESPAÑOL')) {
      if (lang === 'es') lbl.classList.add('active');
      else lbl.classList.remove('active');
    }
  });

  const els = document.querySelectorAll('[data-translate]');
  els.forEach(el => {
    const key = el.getAttribute('data-translate');
    if (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) {
      if (el.tagName === 'OPTION') {
        el.text = TRANSLATIONS[lang][key];
      } else {
        // If there's an image icon inside a button or tag, only replace the text node
        const img = el.querySelector('img');
        const span = el.querySelector('span');
        if (span) {
          span.textContent = TRANSLATIONS[lang][key];
        } else if (img) {
          let textNodeFound = false;
          el.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
              node.textContent = ' ' + TRANSLATIONS[lang][key];
              textNodeFound = true;
            }
          });
          if (!textNodeFound) {
            el.appendChild(document.createTextNode(' ' + TRANSLATIONS[lang][key]));
          }
        } else {
          el.textContent = TRANSLATIONS[lang][key];
        }
      }
    }
  });

  // Dynamically update the HUD room name if worldManager is running!
  if (worldManager && worldManager.player) {
    const hudRoom = document.getElementById('hud-room-name');
    if (hudRoom) {
      const px = worldManager.player.position.x;
      const pz = worldManager.player.position.z;
      const gx = Math.floor((px + worldManager.generator.sectorSize / 2) / worldManager.generator.sectorSize);
      const gz = Math.floor((pz + worldManager.generator.sectorSize / 2) / worldManager.generator.sectorSize);
      const hashVal = Math.abs(worldManager.generator.hash2D(gx, gz));
      const zoneType = hashVal % 10;
      
      const zoneNames = [
        "LOBBY INFINITO", "SHOPPING SILENCIOSO", "HOTEL IMPOSIBLE", 
        "AEROPUERTO DORMIDO", "CENTRO CORPORATIVO", "BIBLIOTECA INTERMINABLE", 
        "CENTRO DE DATOS", "PISCINA SUBTERRÁNEA", "INVERNADERO VACÍO", "GALERÍA DE ARTE"
      ];
      hudRoom.textContent = window.translate(zoneNames[zoneType]);
    }
  }
};

// Fullscreen API Helper
const requestFullscreen = async () => {
  const docEl = document.documentElement;
  try {
    if (docEl.requestFullscreen) {
      await docEl.requestFullscreen();
    } else if (docEl.webkitRequestFullscreen) { /* Safari */
      await docEl.webkitRequestFullscreen();
    } else if (docEl.mozRequestFullScreen) { /* Firefox */
      await docEl.mozRequestFullScreen();
    } else if (docEl.msRequestFullscreen) { /* IE/Edge */
      await docEl.msRequestFullscreen();
    }
  } catch (err) {
    console.warn("Fullscreen request failed:", err);
  }
};

const exitFullscreen = async () => {
  try {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      }
    }
  } catch (err) {
    console.warn("Exit fullscreen failed:", err);
  }
};

// Setup master components
let audioSynth = new WebAudioSynth();
let worldManager = null;

// App configuration states
let isGameStarted = false;

document.addEventListener('DOMContentLoaded', () => {
  runDiagnosticLoader();
  setupUIEventListeners();
  startHUDClock();
});

/**
 * Runs a highly premium retro diagnostic loading boot sequence.
 * Builds tension and introduces the corporate technical atmosphere.
 */
function runDiagnosticLoader() {
  const loaderBar = document.getElementById('loader-bar');
  const loaderStatus = document.getElementById('loader-status');
  const loaderPanel = document.getElementById('loader-panel');
  const actionPanel = document.getElementById('action-panel');

  const logs = [
    { progress: 0, text: 'BOOT SEQUENCE DETECTED. CONFIGURING WEBGL...' },
    { progress: 12, text: 'COMPILING SHADERS & FRAGMENT BUFFERS...' },
    { progress: 28, text: 'CREATING PROCEDURAL PBR FLOOR TEXTURES...' },
    { progress: 45, text: 'DRAWING ACOUSTIC WALLPAPERS & EMISSIVE SIGNS...' },
    { progress: 64, text: 'INITIALIZING SYNTHETIC VENTILATION HUMS...' },
    { progress: 80, text: 'ALLOCATING INFINITE STREAMING RADIUS SECTORS...' },
    { progress: 94, text: 'STABILIZING LIMINAL ATMOSPHERE MATRIX...' },
    { progress: 100, text: 'DIAGNOSTICS COMPLETE. READY FOR DEPLOYMENT.' }
  ];

  let logIndex = 0;
  let currentProgress = 0;

  const interval = setInterval(() => {
    // Increment progress
    currentProgress += Math.floor(Math.random() * 4) + 1;
    if (currentProgress > 100) currentProgress = 100;

    // Apply width to bar
    loaderBar.style.width = `${currentProgress}%`;

    // Process logging statements based on progress targets
    if (logIndex < logs.length && currentProgress >= logs[logIndex].progress) {
      loaderStatus.textContent = `${window.translate(logs[logIndex].text)} (${currentProgress}%)`;
      logIndex++;
    }

    if (currentProgress >= 100) {
      clearInterval(interval);
      setTimeout(() => {
        // Transition panel: fade loader out and slide options in
        loaderPanel.style.display = 'none';
        actionPanel.style.opacity = '0';
        actionPanel.style.display = 'block';
        
        // Quick trigger fade-in
        setTimeout(() => {
          actionPanel.style.transition = 'opacity 0.5s ease';
          actionPanel.style.opacity = '1';

          // Display the PWA installation notification banner if eligible and not dismissed
          const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
          if (!isStandalone && !sessionStorage.getItem('pwa-banner-dismissed')) {
            const banner = document.getElementById('pwa-install-banner');
            if (banner) {
              banner.classList.remove('hidden');
              setTimeout(() => banner.classList.add('visible'), 100);
            }
          }
        }, 50);
      }, 600);
    }
  }, 40);
}

/**
 * Connects UI inputs, settings sliders, PointerLock hooks, and startup triggers.
 */
function setupUIEventListeners() {
  // Elements
  const startScreen = document.getElementById('start-screen');
  const settingsModal = document.getElementById('settings-modal');
  const pauseScreen = document.getElementById('pause-screen');
  
  const btnEnter = document.getElementById('btn-enter');
  const btnResume = document.getElementById('btn-resume');
  const btnSettingsOpen = document.getElementById('btn-settings-open');
  const btnSettingsClose = document.getElementById('btn-settings-close');

  // Multiplayer experimental controls
  const checkMultiplayer = document.getElementById('multiplayer-toggle');
  const inputNickname = document.getElementById('multiplayer-nickname');
  const checkVoice = document.getElementById('multiplayer-voice-toggle');
  const slideTagDistance = document.getElementById('multiplayer-tag-distance');
  const labelTagDistance = document.getElementById('label-tag-distance');
  const inputSignalingUrl = document.getElementById('multiplayer-signaling-url');

  if (checkMultiplayer) {
    const mpEnabled = localStorage.getItem('multiplayer_enabled') === 'true';
    multiplayer.config.enabled = mpEnabled;
    checkMultiplayer.checked = mpEnabled;
    inputNickname.value = multiplayer.config.nickname;
    checkVoice.checked = multiplayer.config.voiceEnabled;
    slideTagDistance.value = multiplayer.config.maxTagDistance;
    labelTagDistance.textContent = `${multiplayer.config.maxTagDistance}m`;
    inputSignalingUrl.value = multiplayer.config.serverUrl;

    checkMultiplayer.addEventListener('change', (e) => {
      multiplayer.config.enabled = e.target.checked;
      localStorage.setItem('multiplayer_enabled', e.target.checked ? 'true' : 'false');
      if (isGameStarted) {
        if (e.target.checked) {
          multiplayer.init(worldManager);
          multiplayer.start();
        } else {
          multiplayer.stop();
        }
      }
    });

    inputNickname.addEventListener('input', (e) => {
      multiplayer.setNickname(e.target.value.toUpperCase());
    });

    checkVoice.addEventListener('change', (e) => {
      multiplayer.setVoiceEnabled(e.target.checked);
    });

    slideTagDistance.addEventListener('input', (e) => {
      labelTagDistance.textContent = `${e.target.value}m`;
      multiplayer.setMaxTagDistance(parseFloat(e.target.value));
    });

    inputSignalingUrl.addEventListener('input', (e) => {
      multiplayer.setServerUrl(e.target.value);
    });
  }

  // Handle mobile orientation detection
  const checkOrientation = () => {
    const rotateOverlay = document.getElementById('rotate-device-overlay');
    if (!rotateOverlay) return;

    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isTouch && window.innerHeight > window.innerWidth) {
      rotateOverlay.style.display = 'flex';
      rotateOverlay.classList.remove('hidden');
    } else {
      rotateOverlay.style.display = 'none';
      rotateOverlay.classList.add('hidden');
    }
  };

  window.addEventListener('resize', checkOrientation);
  window.addEventListener('orientationchange', checkOrientation);
  checkOrientation(); // Initial check

  // Settings screen navigation
  if (btnSettingsOpen) {
    btnSettingsOpen.addEventListener('click', () => {
      settingsModal.classList.remove('hidden');
    });
  }
  if (btnSettingsClose) {
    btnSettingsClose.addEventListener('click', () => {
      settingsModal.classList.add('hidden');
    });
  }

  // Language Switcher Toggle Event
  const languageToggle = document.getElementById('language-toggle');
  if (languageToggle) {
    languageToggle.addEventListener('change', (e) => {
      const selectedLang = e.target.checked ? 'es' : 'en';
      window.switchLanguage(selectedLang);
    });
  }

  // Set default language dynamically on load
  const browserLang = navigator.language.startsWith('es') ? 'es' : 'en';
  window.switchLanguage(browserLang);

  // PWA Install Button & Guide Event Listener
  const btnPwaInstall = document.getElementById('btn-pwa-install');
  const pwaGuideModal = document.getElementById('pwa-guide-modal');
  const btnPwaGuideClose = document.getElementById('btn-pwa-guide-close');
  
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  if (isStandalone && btnPwaInstall) {
    btnPwaInstall.disabled = true;
    btnPwaInstall.style.opacity = '0.5';
    btnPwaInstall.textContent = window.currentLanguage === 'es' ? 'INSTALADO' : 'INSTALLED';
  }

  if (btnPwaInstall) {
    btnPwaInstall.addEventListener('click', async () => {
      if (isStandalone) return;
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User choice outcome: ${outcome}`);
        deferredPrompt = null;
      } else {
        // Show instructions guide modal
        if (pwaGuideModal) {
          pwaGuideModal.classList.remove('hidden');
        }
      }
    });
  }

  if (btnPwaGuideClose && pwaGuideModal) {
    btnPwaGuideClose.addEventListener('click', () => {
      pwaGuideModal.classList.add('hidden');
    });
  }

  // PWA Install Notification Banner Listeners
  const pwaInstallBanner = document.getElementById('pwa-install-banner');
  const btnBannerInstall = document.getElementById('btn-banner-install');
  const btnBannerClose = document.getElementById('btn-banner-close');

  if (btnBannerInstall) {
    btnBannerInstall.addEventListener('click', async () => {
      if (isStandalone) return;
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User choice outcome: ${outcome}`);
        deferredPrompt = null;
        if (pwaInstallBanner) {
          pwaInstallBanner.classList.remove('visible');
          setTimeout(() => pwaInstallBanner.classList.add('hidden'), 500);
        }
      } else {
        if (pwaGuideModal) {
          pwaGuideModal.classList.remove('hidden');
        }
      }
    });
  }

  if (btnBannerClose && pwaInstallBanner) {
    btnBannerClose.addEventListener('click', () => {
      pwaInstallBanner.classList.remove('visible');
      sessionStorage.setItem('pwa-banner-dismissed', 'true');
      setTimeout(() => pwaInstallBanner.classList.add('hidden'), 500);
    });
  }

  // Volume & visual controls - START Screen
  const slideHum = document.getElementById('hum-volume');
  const slideFootstep = document.getElementById('footstep-volume');
  const slideMusic = document.getElementById('music-volume');
  const slideFov = document.getElementById('fov-range');
  const slideBobbing = document.getElementById('bobbing-range');
  const checkFlicker = document.getElementById('flicker-toggle');

  const lblHum = document.getElementById('label-hum');
  const lblFootstep = document.getElementById('label-footstep');
  const lblMusic = document.getElementById('label-music');
  const lblFov = document.getElementById('label-fov');
  const lblBobbing = document.getElementById('label-bobbing');

  // Volume & visual controls - PAUSE Screen
  const pauseSlideHum = document.getElementById('pause-hum-volume');
  const pauseSlideFootstep = document.getElementById('pause-footstep-volume');
  const pauseSlideMusic = document.getElementById('pause-music-volume');
  const pauseSlideFov = document.getElementById('pause-fov-range');

  const pauseLblHum = document.getElementById('pause-label-hum');
  const pauseLblFootstep = document.getElementById('pause-label-footstep');
  const pauseLblMusic = document.getElementById('pause-label-music');
  const pauseLblFov = document.getElementById('pause-label-fov');

  // 1. Synchronized listener helper to mirror startup / pause adjustments
  const syncControls = (type, value) => {
    if (type === 'hum') {
      slideHum.value = value;
      pauseSlideHum.value = value;
      lblHum.textContent = `${value}%`;
      pauseLblHum.textContent = `${value}%`;
      audioSynth.setHumVolume(value);
    } 
    else if (type === 'footstep') {
      slideFootstep.value = value;
      pauseSlideFootstep.value = value;
      lblFootstep.textContent = `${value}%`;
      pauseLblFootstep.textContent = `${value}%`;
      audioSynth.setFootstepVolume(value);
    } 
    else if (type === 'music') {
      slideMusic.value = value;
      pauseSlideMusic.value = value;
      lblMusic.textContent = `${value}%`;
      pauseLblMusic.textContent = `${value}%`;
      audioSynth.setMusicVolume(value);
    }
    else if (type === 'fov') {
      slideFov.value = value;
      pauseSlideFov.value = value;
      lblFov.textContent = `${value}°`;
      pauseLblFov.textContent = `${value}°`;
      if (worldManager) worldManager.setFOV(parseInt(value));
    } 
    else if (type === 'bobbing') {
      slideBobbing.value = value;
      lblBobbing.textContent = `${value}%`;
      if (worldManager) worldManager.setBobbing(parseInt(value));
    }
  };

  // Add event listeners for sliders
  slideHum.addEventListener('input', (e) => syncControls('hum', e.target.value));
  pauseSlideHum.addEventListener('input', (e) => syncControls('hum', e.target.value));

  slideFootstep.addEventListener('input', (e) => syncControls('footstep', e.target.value));
  pauseSlideFootstep.addEventListener('input', (e) => syncControls('footstep', e.target.value));

  slideMusic.addEventListener('input', (e) => syncControls('music', e.target.value));
  pauseSlideMusic.addEventListener('input', (e) => syncControls('music', e.target.value));

  slideFov.addEventListener('input', (e) => syncControls('fov', e.target.value));
  pauseSlideFov.addEventListener('input', (e) => syncControls('fov', e.target.value));

  slideBobbing.addEventListener('input', (e) => syncControls('bobbing', e.target.value));

  checkFlicker.addEventListener('change', (e) => {
    if (worldManager) worldManager.setFlickerActive(e.target.checked);
  });

  // 2. Start button trigger
  btnEnter.addEventListener('click', async () => {
    if (isGameStarted) return;

    // Play synthesized sound ding on menu entry
    btnEnter.disabled = true;
    btnEnter.textContent = window.currentLanguage === 'es' ? 'Estabilizando núcleo...' : 'Stabilizing Core...';

    // Request fullscreen mode if checked
    const fsToggle = document.getElementById('fullscreen-toggle');
    if (fsToggle && fsToggle.checked) {
      await requestFullscreen();
    }

    // A. Initialize Programmatic Audio Graph (complies with browser security guidelines)
    await audioSynth.init();

    // Pass initial settings
    audioSynth.setHumVolume(slideHum.value);
    audioSynth.setFootstepVolume(slideFootstep.value);
    audioSynth.setMusicVolume(slideMusic.value);

    // B. Initialize Graphics pipeline
    const selectRoom = document.getElementById('start-room-select');
    const startZoneType = selectRoom ? parseInt(selectRoom.value) : 0;
    worldManager = new WorldManager('canvas-container', audioSynth, startZoneType);
    
    // Sync initial configuration states
    worldManager.setFOV(parseInt(slideFov.value));
    worldManager.setBobbing(parseInt(slideBobbing.value));
    worldManager.setFlickerActive(checkFlicker.checked);

    // C. Transition Screen Visibilities
    startScreen.classList.add('hidden');
    settingsModal.classList.add('hidden');

    
    // Display CCTV Camera overlays
    document.getElementById('security-hud').classList.remove('hidden');

    isGameStarted = true;

    if (multiplayer.config.enabled) {
      multiplayer.init(worldManager);
      multiplayer.start();
    }
    
    // Show touch overlay controls if on mobile/touch screen
    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isTouch) {
      document.getElementById('mobile-controls-overlay').classList.remove('hidden');
    } else {
      // Synchronously request Pointer Lock immediately using active user click gesture
      try {
        worldManager.renderer.domElement.requestPointerLock();
      } catch (err) {
        console.warn("Main: Programmatic pointer lock request failed, waiting for canvas click.", err);
      }
    }
  });

  // 3. Pause Screen actions
  btnResume.addEventListener('click', () => {
    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isTouch) {
      pauseScreen.classList.add('hidden');
      return;
    }

    if (worldManager && worldManager.renderer) {
      try {
        worldManager.renderer.domElement.requestPointerLock();
      } catch (err) {
        console.warn("Main: Resume pointer lock failed.", err);
      }
    }
  });

  // Manual Esc key press tracking for touch/mobile devices where Pointer Lock doesn't fire
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Escape') {
      if (!isGameStarted) return;
      const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      if (isTouch) {
        if (pauseScreen.classList.contains('hidden')) {
          pauseScreen.classList.remove('hidden');
        } else {
          pauseScreen.classList.add('hidden');
        }
      }
    }
  });

  // 4. PointerLock tracking state changes
  document.addEventListener('pointerlockchange', () => {
    if (!isGameStarted) return;

    const canvas = document.querySelector('canvas');
    if (document.pointerLockElement === canvas) {
      // Game running: Hide pause panel
      pauseScreen.classList.add('hidden');
    } else {
      // Game paused: Display pause settings screen
      pauseScreen.classList.remove('hidden');
    }
  });

  // 5. Exit button actions - Main Menu & Pause Menu
  const btnExitMain = document.getElementById('btn-exit-main');
  if (btnExitMain) {
    btnExitMain.addEventListener('click', () => {
      // Safely redirect or close the tab
      window.location.href = 'about:blank';
      try {
        window.close();
      } catch (err) {}
    });
  }

  const btnExitPause = document.getElementById('btn-exit-pause');
  if (btnExitPause) {
    btnExitPause.addEventListener('click', () => {
      // Exit fullscreen if active
      exitFullscreen();

      // Stop rendering, dispose textures, and clean up ThreeJS WebGL context completely
      if (worldManager) {
        worldManager.destroy();
        worldManager = null;
      }
      multiplayer.stop();
      isGameStarted = false;

      // Hide all game elements, overlays, and restore Start screen panel
      document.getElementById('start-screen').classList.remove('hidden');
      pauseScreen.classList.add('hidden');
      document.getElementById('security-hud').classList.add('hidden');
      document.getElementById('mobile-controls-overlay').classList.add('hidden');

      // Reset the enter button state
      const btnEnter = document.getElementById('btn-enter');
      if (btnEnter) {
        btnEnter.disabled = false;
        btnEnter.textContent = window.translate ? window.translate('btn-enter') : 'Enter Frontrooms';
      }
    });
  }
}

/**
 * Starts ticking digital system clock inside CCTV HUD overlay.
 */
function startHUDClock() {
  const hudTime = document.getElementById('hud-time');
  const hudCamId = document.getElementById('hud-cam-id');

  // Randomize a technical CAM ID on load
  const randomCam = `CAM_${Math.floor(100 + Math.random() * 900)}`;
  if (hudCamId) hudCamId.textContent = randomCam;

  setInterval(() => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 hour should be 12
    const formattedHours = hours.toString().padStart(2, '0');

    if (hudTime) {
      hudTime.textContent = `${formattedHours}:${minutes}:${seconds} ${ampm}`;
    }
  }, 1000);
}
